// src/components/UiFeedbackWidget/useElementPicker.js
//
// Spec 20260925-ui-feedback §6.3, plan §5.6.1 / R-4. Tasks T045.
//
// Pick mode: the user points at the element a remark is about.
//
//   • Listeners sit in the CAPTURE phase on `document`, so they run before React's root
//     listeners. pointerdown, pointerup, mousedown, mouseup and click are all swallowed
//     (preventDefault + stopPropagation + stopImmediatePropagation) outside the widget:
//     MUI Select and Autocomplete open on mousedown, so suppressing only `click` would
//     still let a pointed-at control act (AC-7). `click` picks.
//   • A touch pointerdown picks at once (no hover on a phone).
//   • Keyboard: Esc cancels (and does not reach an open MUI menu); Enter picks
//     document.activeElement; Tab keeps moving focus and the highlight follows `focusin`.
//   • Anything inside [data-ui-feedback-root] is ignored — the widget itself.
//   • The ONLY global style change is a class on <html> (crosshair cursor). No page
//     element's style or attributes are ever touched.
//   • Everything is torn down on pick, Esc, unmount and on a route change without a pick.
//
// The highlight rect only updates when the hovered element CHANGES, which is the
// throttle: a pointermove over the same element costs one comparison.

import { useCallback, useEffect, useRef, useState } from 'react';

export const PICKING_CLASS = 'caro-ui-feedback-picking';
export const WIDGET_ROOT_SELECTOR = '[data-ui-feedback-root]';

const SWALLOWED = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click'];

/**
 * A tap is pointerdown → pointerup → (compat) mousedown/mouseup → click. Picking on the
 * touch pointerdown and tearing down at once would let the trailing click reach the tapped
 * button and RUN it. So after a touch pick, the trailing events are swallowed for a short
 * window by a one-shot guard.
 */
const TOUCH_TRAIL_MS = 700;

function insideWidget(el) {
  return Boolean(el && el.nodeType === 1 && el.closest && el.closest(WIDGET_ROOT_SELECTOR));
}

function swallow(e) {
  e.preventDefault();
  e.stopPropagation();
  if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
}

function rectOf(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * @param {{ onPick: (el: Element) => void, onCancel?: () => void, locationKey?: string }} args
 * @returns {{ picking: boolean, rect: object|null, start: () => void, cancel: () => void }}
 */
export function useElementPicker({ onPick, onCancel, locationKey } = {}) {
  const [picking, setPicking] = useState(false);
  const [rect, setRect] = useState(null);
  const teardownRef = useRef(null);
  const trailRef = useRef(null);
  const hoveredRef = useRef(null);
  const onPickRef = useRef(onPick);
  const onCancelRef = useRef(onCancel);
  onPickRef.current = onPick;
  onCancelRef.current = onCancel;

  const stop = useCallback(() => {
    if (teardownRef.current) {
      teardownRef.current();
      teardownRef.current = null;
    }
    hoveredRef.current = null;
    setPicking(false);
    setRect(null);
  }, []);

  const cancel = useCallback(() => {
    const wasPicking = Boolean(teardownRef.current);
    stop();
    if (wasPicking && onCancelRef.current) onCancelRef.current();
  }, [stop]);

  const start = useCallback(() => {
    if (teardownRef.current) return;
    const doc = document;

    const pick = (el) => {
      stop();
      if (onPickRef.current) onPickRef.current(el);
    };

    const guardTouchTrail = () => {
      if (trailRef.current) trailRef.current();
      const guard = (e) => {
        if (!insideWidget(e.target)) swallow(e);
      };
      SWALLOWED.forEach((type) => doc.addEventListener(type, guard, true));
      const timer = setTimeout(() => trailRef.current && trailRef.current(), TOUCH_TRAIL_MS);
      trailRef.current = () => {
        clearTimeout(timer);
        SWALLOWED.forEach((type) => doc.removeEventListener(type, guard, true));
        trailRef.current = null;
      };
    };

    const hover = (el) => {
      if (!el || insideWidget(el) || el === hoveredRef.current) return;
      hoveredRef.current = el;
      setRect(rectOf(el));
    };

    const onSwallowed = (e) => {
      if (insideWidget(e.target)) return;
      swallow(e);
      if (e.type === 'pointerdown' && e.pointerType === 'touch') {
        const target = e.target;
        pick(target);
        guardTouchTrail();
        return;
      }
      if (e.type === 'click') pick(e.target);
    };

    const onMove = (e) => {
      const el =
        typeof doc.elementFromPoint === 'function' ? doc.elementFromPoint(e.clientX, e.clientY) : e.target;
      hover(el || e.target);
    };

    const onFocusIn = (e) => hover(e.target);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        swallow(e);
        cancel();
        return;
      }
      if (e.key === 'Enter') {
        const el = doc.activeElement;
        if (!el || el === doc.body || insideWidget(el)) return;
        swallow(e);
        pick(el);
      }
    };

    SWALLOWED.forEach((type) => doc.addEventListener(type, onSwallowed, true));
    doc.addEventListener('pointermove', onMove, true);
    doc.addEventListener('focusin', onFocusIn, true);
    doc.addEventListener('keydown', onKey, true);
    doc.documentElement.classList.add(PICKING_CLASS);

    teardownRef.current = () => {
      SWALLOWED.forEach((type) => doc.removeEventListener(type, onSwallowed, true));
      doc.removeEventListener('pointermove', onMove, true);
      doc.removeEventListener('focusin', onFocusIn, true);
      doc.removeEventListener('keydown', onKey, true);
      doc.documentElement.classList.remove(PICKING_CLASS);
    };
    setPicking(true);
  }, [stop, cancel]);

  // A route change without a pick ends pick mode.
  const lastKeyRef = useRef(locationKey);
  useEffect(() => {
    if (lastKeyRef.current !== locationKey) {
      lastKeyRef.current = locationKey;
      if (teardownRef.current) cancel();
    }
  }, [locationKey, cancel]);

  // Unmount: nothing may survive the widget.
  useEffect(
    () => () => {
      if (teardownRef.current) {
        teardownRef.current();
        teardownRef.current = null;
      }
      if (trailRef.current) trailRef.current();
    },
    [],
  );

  return { picking, rect, start, cancel };
}

export default useElementPicker;
